import { state, api, $, $$, toast, escHtml } from './core';
import { askConfirm } from './dialogs';
import type { RandLineElements, RandLineLayouts, RandLineMedia, RandMachines, RandProductionLines } from './tipuri-baza';

/** /api/line-detail/<linie> (routes/machines.py:api_line_detail_layout) */
interface DateLinie {
    line: RandProductionLines | null;
    layout: RandLineLayouts | null;
    elements: RandLineElements[];
    media: RandLineMedia[];
    machines: Pick<RandMachines, 'id' | 'name' | 'machine_type' | 'manufacturer' | 'model' | 'status'>[];
}

// ─── LINE LAYOUT DETAIL ──────────────────────────────────────────

export let _llData: DateLinie | null = null;
export let _llLineId: string | null = null;
export let _llTab: string | undefined = 'layout';
export let _llEditMode = false;

export async function openLineLayoutDetail(lineId: string|null, product: string) {
    _llLineId = lineId;
    _llTab = 'layout';
    _llEditMode = false;
    document.getElementById('ll-line-id')!.textContent = lineId;
    document.getElementById('ll-line-product')!.textContent = product || '';
    document.getElementById('ll-overlay')!.classList.remove('hidden');
    document.getElementById('ll-content')!.innerHTML = '<div style="text-align:center;padding:40px;color:var(--text-muted)">Se incarca...</div>';

    try {
        _llData = await api(`/api/line-detail/${lineId}`);
    } catch (e) {
        document.getElementById('ll-content')!.innerHTML = `<div class="alarm-empty">Eroare: ${(e as Error).message}</div>`;
        return;
    }
    _renderLLTabs();
    _renderLLContent();
}

export function _renderLLTabs() {
    document.querySelectorAll('#ll-tabs .detail-tab').forEach(t => {
        t.classList.toggle('active', (t as HTMLElement).dataset.lltab === _llTab);
        (t as HTMLElement).onclick = () => { _llTab = (t as HTMLElement).dataset.lltab; _renderLLTabs(); _renderLLContent(); };
    });
}

export function _renderLLContent() {
    const el = document.getElementById('ll-content');
    if (!_llData) { el!.innerHTML = ''; return; }
    const isMaster = state.currentUser && state.currentUser.role === 'master';

    if (_llTab === 'layout') {
        _renderLLLayout(el, isMaster);
    } else if (_llTab === 'elements') {
        _renderLLElements(el, isMaster);
    } else if (_llTab === 'media') {
        _renderLLMedia(el, isMaster);
    }
}

export function _renderLLLayout(el: HTMLElement|null, isMaster: boolean) {
    const layout = _llData!.layout;
    const elements = _llData!.elements || [];
    let html = '';

    if (isMaster) {
        html += `<div class="ll-upload-bar">
            <form id="ll-layout-upload" style="display:inline-flex;gap:8px;align-items:center">
                <input type="file" name="file" accept=".svg,.png,.jpg,.jpeg,.webp" required>
                <button type="submit" class="btn btn-sm btn-primary">Incarca layout</button>
            </form>
            ${layout ? `<button type="button" class="btn btn-sm ll-edit-toggle" style="margin-left:auto">${_llEditMode ? 'Gata editare' : 'Editeaza elemente'}</button>` : ''}
        </div>`;
    }

    if (!layout) {
        html += '<div class="ll-no-layout"><div class="alarm-empty" style="padding:60px">Niciun layout incarcat.<br>Incarcati un fisier SVG sau imagine cu layout-ul liniei.</div></div>';
    } else {
        const isImg = ['png','jpg','jpeg','webp','gif'].includes(layout.file_type as string);
        html += `<div class="ll-layout-container" id="ll-layout-container">
            <div class="ll-layout-wrap" id="ll-layout-wrap">`;
        if (isImg) {
            html += `<img src="/uploads/${escHtml(layout.filename)}" class="ll-layout-img" id="ll-layout-img" draggable="false">`;
        } else {
            html += `<object data="/uploads/${escHtml(layout.filename)}" type="image/svg+xml" class="ll-layout-img" id="ll-layout-img"></object>`;
        }
        elements.forEach((elem) => {
            const status = _getElementStatus(elem);
            const statusCls = status === 'alarm' ? 'elem-alarm' : status === 'warning' ? 'elem-warn' : 'elem-ok';
            const icon = _getElementIcon(elem);
            const sensorVal = elem.sensor_key && elem.machine_id ? getSensorValue(elem.machine_id, elem.sensor_key) : null;
            const valLabel = sensorVal != null ? ` ${parseFloat(String(sensorVal)).toFixed(1)}` : '';
            html += `<div class="ll-element ${statusCls} ${_llEditMode ? 'draggable' : ''}" data-elem-id="${elem.id}"
                style="left:${elem.pos_x}%;top:${elem.pos_y}%"
                title="${escHtml(elem.name)}${valLabel ? ' = ' + valLabel : ''}">
                <span class="ll-elem-icon">${icon}</span>
                <span class="ll-elem-label">${escHtml(elem.name)}${valLabel}</span>
            </div>`;
        });
        html += '</div></div>';
    }

    el!.innerHTML = html;

    if (isMaster) {
        const form = document.getElementById('ll-layout-upload');
        if (form) {
            form.addEventListener('submit', async (e) => {
                e.preventDefault();
                const fd = new FormData(form as HTMLFormElement);
                try {
                    const res = await fetch(`/api/line-detail/${_llLineId}/layout`, {method:'POST', body:fd, credentials:'include'});
                    const data = await res.json();
                    if (res.ok) { toast('Layout incarcat'); _llData!.layout = data.layout; _renderLLContent(); }
                    else toast(data.error || 'Eroare', 'error');
                } catch (err) { toast('Eroare: ' + (err as Error).message, 'error'); }
            });
        }
        const editBtn = el!.querySelector('.ll-edit-toggle');
        if (editBtn) {
            editBtn.addEventListener('click', () => { _llEditMode = !_llEditMode; _renderLLContent(); });
        }
    }

    if (_llEditMode) {
        _initElementDrag();
        const container = document.getElementById('ll-layout-wrap');
        if (container) {
            container.addEventListener('dblclick', (e) => {
                const rect = container.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width * 100).toFixed(2);
                const y = ((e.clientY - rect.top) / rect.height * 100).toFixed(2);
                _showAddElementDialog(x, y);
            });
        }
    }
}

export function _getElementIcon(elem: Pick<RandLineElements, 'element_type' | 'icon'>) {
    const icons = {sensor:'📡', filter:'🔽', valve:'🔧', pump:'⛽', motor:'⚙️', pipe_water:'💧', pipe_air:'💨', pipe_oil:'🛢️', temperature:'🌡️', pressure:'🔴', flow:'🌊', other:'📍'};
    return (icons as Record<string, string>)[elem.element_type] || elem.icon || '📍';
}

export function _getElementStatus(elem: Pick<RandLineElements, 'machine_id' | 'sensor_key'>) {   // fără ele: „ok”
    if (!elem.machine_id || !elem.sensor_key) return 'ok';
    const v = getSensorValue(elem.machine_id, elem.sensor_key);
    if (v == null) return 'ok';
    const alarms = (state.sensorAlarms || []).filter(a =>
        a.machine_id === elem.machine_id && a.sensor_key === elem.sensor_key && a.active
    );
    if (alarms.some(a => a.severity === 'critical' || a.severity === 'high')) return 'alarm';
    if (alarms.length > 0) return 'warning';
    return 'ok';
}

export function _initElementDrag() {
    document.querySelectorAll('.ll-element.draggable').forEach(el => {
        let startX: number, startY: number, origLeft: number, origTop: number;
        el.addEventListener('mousedown', (ev) => {
            const e = ev as MouseEvent;
            if (e.button !== 0) return;
            e.preventDefault();
            const container = document.getElementById('ll-layout-wrap');
            const rect = container!.getBoundingClientRect();
            startX = e.clientX; startY = e.clientY;
            origLeft = parseFloat((el as HTMLElement).style.left); origTop = parseFloat((el as HTMLElement).style.top);

            const onMove = (ev: { clientX: number; clientY: number; }) => {
                const dx = (ev.clientX - startX) / rect.width * 100;
                const dy = (ev.clientY - startY) / rect.height * 100;
                (el as HTMLElement).style.left = Math.max(0, Math.min(100, origLeft + dx)) + '%';
                (el as HTMLElement).style.top = Math.max(0, Math.min(100, origTop + dy)) + '%';
            };
            const onUp = async () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                const newX = parseFloat((el as HTMLElement).style.left).toFixed(2);
                const newY = parseFloat((el as HTMLElement).style.top).toFixed(2);
                const elemId = (el as HTMLElement).dataset.elemId;
                try {
                    await fetch(`/api/line-detail/${_llLineId}/elements/${elemId}`, {
                        method:'PUT', credentials:'include',
                        headers:{'Content-Type':'application/json'},
                        body: JSON.stringify({pos_x: parseFloat(newX), pos_y: parseFloat(newY)})
                    });
                } catch(err) { console.warn('Element position save failed:', err); }
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        });
        el.addEventListener('contextmenu', async (e) => {
            e.preventDefault();
            if (await askConfirm(`Sterge elementul "${(el as HTMLElement).title}"?`)) {
                const elemId = (el as HTMLElement).dataset.elemId;
                fetch(`/api/line-detail/${_llLineId}/elements/${elemId}`, {method:'DELETE', credentials:'include'})
                    .then(() => {
                        _llData!.elements = _llData!.elements.filter((e) => String(e.id) != elemId);   // id-ul vine ca text din data-elem-id
                        _renderLLContent();
                    });
            }
        });
    });
}

export async function _showAddElementDialog(x: string, y: string) {
    const machines = (_llData!.machines || []);
    const machOpts = machines.map((m) => `<option value="${m.id}">${m.id} - ${escHtml(m.name || m.model || '')}</option>`).join('');
    const typeOpts = ['sensor','filter','valve','pump','motor','pipe_water','pipe_air','pipe_oil','temperature','pressure','flow','other']
        .map(t => `<option value="${t}">${t}</option>`).join('');

    const dialog = document.createElement('div');
    dialog.className = 'll-add-dialog';
    dialog.innerHTML = `<div class="ll-add-dialog-inner">
        <h4>Adauga element la pozitia (${x}%, ${y}%)</h4>
        <label>Tip: <select id="ll-add-type">${typeOpts}</select></label>
        <label>Nume: <input type="text" id="ll-add-name" placeholder="ex: Senzor presiune hidraulic"></label>
        <label>Utilaj: <select id="ll-add-machine"><option value="">-- optional --</option>${machOpts}</select></label>
        <label>Cheie senzor: <input type="text" id="ll-add-sensor" placeholder="ex: pressure, temperature, flow"></label>
        <label>Descriere: <input type="text" id="ll-add-desc" placeholder="optional"></label>
        <div class="ll-add-btns">
            <button class="btn btn-sm" onclick="this.closest('.ll-add-dialog').remove()">Anuleaza</button>
            <button class="btn btn-sm btn-primary" id="ll-add-save">Salveaza</button>
        </div>
    </div>`;
    document.body.appendChild(dialog);
    document.getElementById('ll-add-save')!.addEventListener('click', async () => {
        const payload = {
            element_type: (document.getElementById('ll-add-type') as HTMLInputElement).value,
            name: (document.getElementById('ll-add-name') as HTMLInputElement).value || 'Element nou',
            machine_id: (document.getElementById('ll-add-machine') as HTMLInputElement).value || null,
            sensor_key: (document.getElementById('ll-add-sensor') as HTMLInputElement).value || null,
            description: (document.getElementById('ll-add-desc') as HTMLInputElement).value || '',
            layout_id: _llData!.layout ? _llData!.layout.id : null,
            pos_x: parseFloat(x), pos_y: parseFloat(y)
        };
        try {
            const res = await fetch(`/api/line-detail/${_llLineId}/elements`, {
                method:'POST', credentials:'include',
                headers:{'Content-Type':'application/json'},
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (res.ok) {
                _llData!.elements.push(data.element);
                toast('Element adaugat');
                dialog.remove();
                _renderLLContent();
            } else toast(data.error || 'Eroare', 'error');
        } catch(err) { toast('Eroare: ' + (err as Error).message, 'error'); }
    });
}

export function _renderLLElements(el: HTMLElement|null, isMaster: boolean) {
    const elements = _llData!.elements || [];
    let html = '<div class="ll-elements-list">';
    if (elements.length === 0) {
        html += '<div class="alarm-empty" style="padding:30px">Niciun element definit.<br>Deschideti tab-ul Layout si dublu-click pe desen pentru a adauga elemente.</div>';
    } else {
        html += `<table class="ll-elem-table"><thead><tr><th>Tip</th><th>Nume</th><th>Utilaj</th><th>Senzor</th><th>Valoare</th><th>Stare</th>${isMaster ? '<th></th>' : ''}</tr></thead><tbody>`;
        elements.forEach((elem) => {
            const icon = _getElementIcon(elem);
            const status = _getElementStatus(elem);
            const statusLabel = status === 'alarm' ? '🔴 Alarma' : status === 'warning' ? '🟡 Avertisment' : '🟢 OK';
            const val = elem.sensor_key && elem.machine_id ? getSensorValue(elem.machine_id, elem.sensor_key) : null;
            const valStr = val != null ? parseFloat(String(val)).toFixed(2) : '—';
            html += `<tr class="ll-elem-row ${status !== 'ok' ? 'll-elem-alert' : ''}">
                <td>${icon} ${escHtml(elem.element_type)}</td>
                <td><strong>${escHtml(elem.name)}</strong>${elem.description ? '<br><small>' + escHtml(elem.description) + '</small>' : ''}</td>
                <td>${escHtml(elem.machine_id || '—')}</td>
                <td>${escHtml(elem.sensor_key || '—')}</td>
                <td>${valStr}</td>
                <td>${statusLabel}</td>
                ${isMaster ? `<td><span class="doc-ch-delete" data-elem-id="${elem.id}" title="Sterge">&times;</span></td>` : ''}
            </tr>`;
        });
        html += '</tbody></table>';
    }
    html += '</div>';
    el!.innerHTML = html;

    if (isMaster) {
        el!.querySelectorAll<HTMLElement>('.doc-ch-delete').forEach((btn) => {
            btn.addEventListener('click', async () => {
                const eid = btn.dataset.elemId;
                if (!await askConfirm('Sterge elementul?')) return;
                await fetch(`/api/line-detail/${_llLineId}/elements/${eid}`, {method:'DELETE', credentials:'include'});
                _llData!.elements = _llData!.elements.filter((e) => String(e.id) != eid);
                _renderLLContent();
            });
        });
    }
}

export function _renderLLMedia(el: HTMLElement|null, isMaster: boolean) {
    const media = _llData!.media || [];
    let html = '';

    if (isMaster) {
        html += `<div class="ll-upload-bar">
            <form id="ll-media-upload" style="display:inline-flex;gap:8px;align-items:center">
                <input type="file" name="file" accept="image/*,video/mp4,video/webm" required>
                <input type="text" name="description" placeholder="Descriere" style="width:200px">
                <button type="submit" class="btn btn-sm btn-primary">Incarca</button>
            </form>
        </div>`;
    }

    if (media.length === 0) {
        html += '<div class="alarm-empty" style="padding:30px">Nicio poza sau video.<br>Incarcati imagini si videoclipuri cu linia in functionare.</div>';
    } else {
        html += '<div class="ll-media-grid">';
        media.forEach((m) => {
            if (m.media_type === 'video') {
                html += `<div class="ll-media-item">
                    <video src="/uploads/${escHtml(m.filename)}" controls preload="metadata" class="ll-media-vid"></video>
                    <div class="ll-media-desc">${escHtml(m.description || m.original_name || '')}</div>
                    ${isMaster ? `<span class="ll-media-del" data-media-id="${m.id}">&times;</span>` : ''}
                </div>`;
            } else {
                html += `<div class="ll-media-item">
                    <img src="/uploads/${escHtml(m.filename)}" class="ll-media-img" onclick="document.getElementById('lightbox-img').src=this.src;document.getElementById('lightbox-overlay').style.display='flex'">
                    <div class="ll-media-desc">${escHtml(m.description || m.original_name || '')}</div>
                    ${isMaster ? `<span class="ll-media-del" data-media-id="${m.id}">&times;</span>` : ''}
                </div>`;
            }
        });
        html += '</div>';
    }

    el!.innerHTML = html;

    if (isMaster) {
        const form = document.getElementById('ll-media-upload');
        if (form) {
            form.addEventListener('submit', async (e) => {
                e.preventDefault();
                const fd = new FormData(form as HTMLFormElement);
                try {
                    const res = await fetch(`/api/line-detail/${_llLineId}/media`, {method:'POST', body:fd, credentials:'include'});
                    const data = await res.json();
                    if (res.ok) { toast('Media incarcat'); _llData!.media.push(data.media); _renderLLContent(); }
                    else toast(data.error || 'Eroare', 'error');
                } catch (err) { toast('Eroare: ' + (err as Error).message, 'error'); }
            });
        }
        el!.querySelectorAll<HTMLElement>('.ll-media-del').forEach((btn) => {
            btn.addEventListener('click', async () => {
                if (!await askConfirm('Sterge fisierul media?')) return;
                const mid = btn.dataset.mediaId;
                await fetch(`/api/line-detail/${_llLineId}/media/${mid}`, {method:'DELETE', credentials:'include'});
                _llData!.media = _llData!.media.filter((m) => String(m.id) != mid);
                _renderLLContent();
            });
        });
    }
}
